import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGameStore } from "@/store/gameStore";
import { useGameDetail } from "@/hooks/useGameDetail";
import { useGamePolling } from "@/hooks/useGamePolling";
import { fetchAuthed } from "@/utils/api";
import type {
  GameStatusResponse,
  PlayerStanding,
  SubmissionView,
  PlayerInfo,
  GameStatus,
} from "@/types/api";
import Spinner from "@/components/ui/Spinner";
import Button from "@/components/ui/Button";
import Badge from "@/components/ui/Badge";
import Modal from "@/components/ui/Modal";
import InviteCodeShare from "@/components/game/InviteCodeShare";
import ItemChecklist from "@/components/game/ItemChecklist";
import Standings from "@/components/game/Standings";
import CountdownTimer from "@/components/game/CountdownTimer";
import PlayerList from "@/components/game/PlayerList";
import CameraCapture from "@/components/submission/CameraCapture";
import ModerationQueue from "@/components/submission/ModerationQueue";
import ProgressRing from "@/components/game/ProgressRing";
import Podium from "@/components/game/Podium";
import ActivityFeed from "@/components/game/ActivityFeed";
import ShareRecap from "@/components/game/ShareRecap";
import { celebrateApproval, celebrateAllComplete } from "@/utils/confetti";
import {
  ListChecks, BarChart3, ShieldCheck, Users, Rss,
  Play, Square, CheckSquare, XCircle, LogOut,
  Crown, Home, RefreshCw, AlertTriangle,
} from "lucide-react";

type Tab = "items" | "standings" | "moderate" | "players" | "feed";

export default function GamePage() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const session = useGameStore((s) => s.getSession(gameId ?? ""));
  const removeSession = useGameStore((s) => s.removeSession);

  const { game, loading, error, errorStatus, refresh } = useGameDetail(gameId ?? "");
  const updateSession = useGameStore((s) => s.updateSession);

  const [standings, setStandings] = useState<PlayerStanding[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [status, setStatus] = useState<GameStatus | null>(null);
  const prevStatusRef = useRef<GameStatus | null>(null);
  const [submissionsLocked, setSubmissionsLocked] = useState(false);
  const [cutoffAt, setCutoffAt] = useState<number | undefined>();

  const [submissions, setSubmissions] = useState<SubmissionView[]>([]);
  const [players, setPlayers] = useState<PlayerInfo[]>([]);
  const [tab, setTab] = useState<Tab>("items");
  const [captureItem, setCaptureItem] = useState<string | null>(null);

  const [confirmAction, setConfirmAction] = useState<
    "start" | "end" | "done" | "leave" | "cancel" | "solo-mod-warn" | null
  >(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const prevApprovedRef = useRef<number>(0);
  const prevPlayerCountRef = useRef<number>(0);
  const prevPendingCountRef = useRef<number>(0);
  const [modRefreshTrigger, setModRefreshTrigger] = useState(0);

  const isOwner = session?.role === "owner";
  const isModerator = session?.isModerator ?? false;
  const gameStatus = status ?? game?.status ?? "lobby";
  const isActive = gameStatus === "active" || gameStatus === "ended";

  // Confetti on new approvals
  useEffect(() => {
    if (!session || !game) return;
    const myApproved = submissions.filter(
      (s) => s.playerId === session.playerId && s.moderationStatus === "approved",
    ).length;
    const totalItems = game.items.length;
    if (myApproved > prevApprovedRef.current && prevApprovedRef.current > 0) {
      if (myApproved >= totalItems && totalItems > 0) {
        celebrateAllComplete();
      } else {
        celebrateApproval();
      }
    }
    prevApprovedRef.current = myApproved;
  }, [submissions, session, game]);

  // Fetch submissions
  const refreshSubmissions = useCallback(async () => {
    if (!gameId || !session) return;
    try {
      const data = await fetchAuthed<{ submissions: SubmissionView[] }>(
        gameId,
        `/games/${gameId}/submissions`,
      );
      setSubmissions(data.submissions);
    } catch {
      // Swallow
    }
  }, [gameId, session]);

  // Fetch players
  const refreshPlayers = useCallback(async () => {
    if (!gameId || !session) return;
    try {
      const data = await fetchAuthed<{ players: PlayerInfo[] }>(
        gameId,
        `/games/${gameId}/players`,
      );
      setPlayers(data.players);
    } catch {
      // Swallow
    }
  }, [gameId, session]);

  // Update state from polling
  const handlePollUpdate = useCallback(
    (data: GameStatusResponse) => {
      const statusChanged = prevStatusRef.current !== null && prevStatusRef.current !== data.status;
      prevStatusRef.current = data.status;

      setStandings(data.players);
      setStatus(data.status);
      setSubmissionsLocked(data.submissionsLocked);
      setCutoffAt(data.submissionsCutoffAt);
      if (data.pendingSubmissions !== undefined) {
        setPendingCount(data.pendingSubmissions);
        // Signal ModerationQueue to refresh when pending count changes
        if (data.pendingSubmissions !== prevPendingCountRef.current) {
          prevPendingCountRef.current = data.pendingSubmissions;
          setModRefreshTrigger((c) => c + 1);
        }
      }
      // On status change, re-fetch full game detail (e.g. items become visible)
      if (statusChanged) {
        refresh();
        // Auto-switch to feed tab when game ends
        if (data.status === "ended") {
          setTab("feed");
        }
      }
      // Refresh submissions so ItemChecklist stays current
      if (data.status === "active" || data.status === "ended") {
        refreshSubmissions();
      }
      // Detect player count changes → refresh player list
      if (data.players.length !== prevPlayerCountRef.current) {
        prevPlayerCountRef.current = data.players.length;
        refreshPlayers();
      }
      // Sync moderator/role state and gameStatus to session store
      if (gameId) {
        const updates: Record<string, unknown> = {};
        if (data.callerIsModerator !== undefined) {
          updates.isModerator = data.callerIsModerator;
        }
        if (data.callerRole) {
          updates.role = data.callerRole;
        }
        updates.gameStatus = data.status;
        if (Object.keys(updates).length > 0) {
          updateSession(gameId, updates);
        }
      }
    },
    [gameId, updateSession, refreshSubmissions, refresh, refreshPlayers],
  );

  useGamePolling({
    gameId: gameId ?? "",
    enabled: !!session && gameStatus !== "done" && gameStatus !== "purged",
    onUpdate: handlePollUpdate,
  });

  // Initialize standings from game detail and sync session
  useEffect(() => {
    if (game) {
      // On initial load of a done game, show the feed tab
      if (prevStatusRef.current === null && game.status === "done") {
        setTab("feed");
      }
      setStandings(game.players);
      setStatus(game.status);
      setCutoffAt(game.submissionsCutoffAt);
      prevStatusRef.current = game.status;
      prevPlayerCountRef.current = game.players.length;
      // Sync moderator/role state and gameStatus from server
      if (gameId) {
        const updates: Record<string, unknown> = {};
        if (game.callerIsModerator !== undefined) {
          updates.isModerator = game.callerIsModerator;
        }
        if (game.callerRole) {
          updates.role = game.callerRole;
        }
        updates.gameStatus = game.status;
        if (Object.keys(updates).length > 0) {
          updateSession(gameId, updates);
        }
      }
    }
  }, [game, gameId, updateSession]);

  useEffect(() => {
    if (isActive || gameStatus === "done") {
      refreshSubmissions();
    }
    refreshPlayers();
  }, [gameStatus, isActive, refreshSubmissions, refreshPlayers]);

  // Actions
  const executeAction = async () => {
    if (!gameId || !confirmAction) return;
    setActionLoading(true);
    setActionError(null);
    try {
      if (confirmAction === "solo-mod-warn") {
        // User acknowledged the warning — proceed to start
        setConfirmAction(null);
        setConfirmAction("start");
        setActionLoading(false);
        return;
      }
      if (confirmAction === "start") {
        await fetchAuthed(gameId, `/games/${gameId}/start`, {
          method: "POST",
        });
      } else if (confirmAction === "end") {
        await fetchAuthed(gameId, `/games/${gameId}/end`, {
          method: "POST",
        });
      } else if (confirmAction === "done") {
        await fetchAuthed(gameId, `/games/${gameId}/done`, {
          method: "POST",
        });
        if (gameId) updateSession(gameId, { gameStatus: "done" });
        navigate("/");
        return;
      } else if (confirmAction === "cancel") {
        await fetchAuthed(gameId, `/games/${gameId}/cancel`, {
          method: "POST",
        });
        removeSession(gameId);
        navigate("/");
        return;
      } else if (confirmAction === "leave") {
        removeSession(gameId);
        navigate("/");
        return;
      }
      setConfirmAction(null);
      refresh();
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Action failed. Please try again.";
      setActionError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  // No session
  if (!session) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <AlertTriangle className="mb-3 h-10 w-10 text-warning" />
        <p className="text-text-secondary">
          You don&apos;t have access to this game.
        </p>
        <Button
          variant="primary"
          onClick={() => navigate("/")}
          className="mt-4"
        >
          <Home className="h-4 w-4" />
          Go Home
        </Button>
      </div>
    );
  }

  if (loading && !game) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    const isGone = errorStatus === 404 || errorStatus === 401 || errorStatus === 403;
    if (isGone && gameId) {
      removeSession(gameId);
    }
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <AlertTriangle className="mb-3 h-10 w-10 text-warning" />
        <p className="text-lg font-semibold text-text">
          {isGone ? "Game not found" : "Something went wrong"}
        </p>
        <p className="mt-2 text-text-secondary">
          {isGone
            ? "This game may have been deleted or is no longer available."
            : error}
        </p>
        {isGone ? (
          <Button
            variant="primary"
            onClick={() => navigate("/")}
            className="mt-6"
          >
            <Home className="h-4 w-4" />
            Go Home
          </Button>
        ) : (
          <Button variant="ghost" onClick={refresh} className="mt-6">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (!game) return null;

  const playerNames: Record<string, string> = {};
  for (const p of players) {
    playerNames[p.playerId] = p.displayName;
  }
  // Also include standings names as fallback
  for (const s of standings) {
    if (!playerNames[s.playerId]) {
      playerNames[s.playerId] = s.displayName;
    }
  }

  const captureItemObj = captureItem
    ? game.items.find((i) => i.itemId === captureItem)
    : null;

  const tabs: { key: Tab; label: string; icon: React.ReactNode; badge?: number; visible: boolean }[] =
    [
      { key: "items", label: "Items", icon: <ListChecks className="h-4 w-4" />, visible: true },
      { key: "standings", label: "Standings", icon: <BarChart3 className="h-4 w-4" />, visible: true },
      {
        key: "moderate",
        label: "Moderate",
        icon: <ShieldCheck className="h-4 w-4" />,
        badge: pendingCount,
        visible: (isOwner || isModerator) && isActive,
      },
      {
        key: "feed",
        label: "Feed",
        icon: <Rss className="h-4 w-4" />,
        visible: isActive || gameStatus === "done",
      },
      { key: "players", label: "Players", icon: <Users className="h-4 w-4" />, visible: isOwner },
    ];

  const statusLabel: Record<string, { text: string; variant: "accent" | "primary" | "warning" | "error" | "muted" }> = {
    lobby: { text: "Lobby", variant: "primary" },
    active: { text: "Active", variant: "accent" },
    ended: { text: "Ended", variant: "warning" },
    done: { text: "Done", variant: "muted" },
    purged: { text: "Purged", variant: "error" },
  };

  const statusInfo = statusLabel[gameStatus] ?? { text: gameStatus, variant: "muted" as const };

  const myStanding = standings.find((s) => s.playerId === session.playerId);
  const myApprovedCount = myStanding?.approvedCount ?? 0;
  const totalItems = game.items.length;
  const progressPct = totalItems > 0 ? Math.round((myApprovedCount / totalItems) * 100) : 0;

  return (
    <div>
      {/* Header */}
      {isActive || gameStatus === "done" ? (
        <div className="flex animate-fade-in-up items-center gap-4">
          <ProgressRing percentage={progressPct} size={100} strokeWidth={8}>
            <span className="text-2xl font-extrabold tabular-nums text-text">
              {myApprovedCount}
            </span>
            <span className="text-[10px] font-bold text-text-secondary uppercase">
              of {totalItems}
            </span>
          </ProgressRing>
          <div className="min-w-0 flex-1">
            <h1 className="truncate font-display text-xl font-extrabold text-text">
              {game.title}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <Badge variant={statusInfo.variant}>{statusInfo.text}</Badge>
              {isOwner && <Badge variant="primary"><Crown className="mr-0.5 inline h-3 w-3" /> owner</Badge>}
              {isModerator && !isOwner && <Badge variant="accent"><ShieldCheck className="mr-0.5 inline h-3 w-3" /> mod</Badge>}
            </div>
            {gameStatus === "active" && cutoffAt && (
              <div className="mt-2">
                <CountdownTimer targetEpochMs={cutoffAt} />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <h1 className="font-display text-2xl font-extrabold text-text">
            {game.title}
          </h1>
          <div className="mt-1 flex items-center gap-2">
            <Badge variant={statusInfo.variant}>{statusInfo.text}</Badge>
            {isOwner && <Badge variant="primary"><Crown className="mr-0.5 inline h-3 w-3" /> owner</Badge>}
            {isModerator && !isOwner && <Badge variant="accent"><ShieldCheck className="mr-0.5 inline h-3 w-3" /> mod</Badge>}
          </div>
        </div>
      )}

      {/* Lobby state */}
      {gameStatus === "lobby" && (
        <div className="mt-4 space-y-4">
          <InviteCodeShare slug={session.slug} />
          {game.instructions && (
            <div className="rounded-xl border border-border bg-surface p-3">
              <p className="text-sm font-medium uppercase text-text-secondary">
                Instructions
              </p>
              <p className="mt-1 text-base text-text">{game.instructions}</p>
            </div>
          )}
          {!isOwner && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1 rounded-2xl border-2 border-primary/20 bg-primary/5 p-5 text-center">
                  <p className="text-4xl font-extrabold text-primary">{players.length}</p>
                  <p className="mt-1 text-sm font-bold text-text-secondary uppercase tracking-wide">Players</p>
                </div>
                <div className="flex-1 rounded-2xl border-2 border-accent/20 bg-accent/5 p-5 text-center">
                  <p className="text-4xl font-extrabold text-accent">{(game.totalItemCount ?? game.items.length) || "?"}</p>
                  <p className="mt-1 text-sm font-bold text-text-secondary uppercase tracking-wide">Items</p>
                </div>
              </div>
              <PlayerList
                gameId={gameId!}
                players={players}
                isOwner={false}
                onUpdate={refreshPlayers}
              />
              <div className="flex flex-col items-center py-6">
                <div className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:0ms]" />
                  <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:150ms]" />
                  <span className="h-2.5 w-2.5 animate-bounce rounded-full bg-primary [animation-delay:300ms]" />
                </div>
                <p className="mt-3 text-base font-medium text-text-secondary">
                  Waiting for the host to start...
                </p>
              </div>
            </div>
          )}
          {isOwner && (
            <>
              <PlayerList
                gameId={gameId!}
                players={players}
                isOwner={isOwner}
                onUpdate={refreshPlayers}
              />
              <Button
                variant="primary"
                onClick={() => {
                  const moderators = players.filter((p) => p.isModerator);
                  const hasModeration = game.moderationMode !== "none";
                  const soloModIsPlayer = hasModeration && moderators.length === 1;
                  if (soloModIsPlayer) {
                    setConfirmAction("solo-mod-warn");
                  } else {
                    setConfirmAction("start");
                  }
                }}
                className="w-full"
                disabled={game.items.length === 0}
              >
                <Play className="h-4 w-4" />
                Start Game
              </Button>
            </>
          )}
          {isOwner ? (
            <button
              onClick={() => setConfirmAction("cancel")}
              className="inline-flex w-full items-center justify-center gap-1.5 text-center text-base text-text-secondary hover:text-error"
            >
              <XCircle className="h-4 w-4" />
              Cancel Game
            </button>
          ) : (
            <button
              onClick={() => setConfirmAction("leave")}
              className="inline-flex w-full items-center justify-center gap-1.5 text-center text-base text-text-secondary hover:text-error"
            >
              <LogOut className="h-4 w-4" />
              Leave Game
            </button>
          )}
        </div>
      )}

      {/* Active / Ended / Done — tabbed view */}
      {(isActive || gameStatus === "done") && (
        <>
          {/* Tab bar */}
          <div className="mt-4 flex gap-1 overflow-x-auto rounded-xl bg-surface-sunken p-1">
            {tabs
              .filter((t) => t.visible)
              .map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`relative flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    tab === t.key
                      ? "bg-surface text-text shadow-sm"
                      : "text-text-secondary hover:text-text"
                  }`}
                >
                  <span className="inline-flex items-center gap-1.5">
                    {t.icon}
                    {t.label}
                  </span>
                  {t.badge != null && t.badge > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-bold text-white">
                      {t.badge}
                    </span>
                  )}
                </button>
              ))}
          </div>

          {/* Tab content */}
          <div className="mt-4">
            {tab === "items" && (
              <ItemChecklist
                items={game.items}
                submissions={submissions}
                myPlayerId={session.playerId}
                canSubmit={gameStatus === "active" && !submissionsLocked}
                onCapture={(itemId) => setCaptureItem(itemId)}
              />
            )}
            {tab === "standings" && (
              <>
                {(gameStatus === "ended" || gameStatus === "done") && standings.length >= 2 && (
                  <Podium players={standings} myPlayerId={session.playerId} />
                )}
                <Standings
                  players={standings}
                  myPlayerId={session.playerId}
                />
              </>
            )}
            {tab === "moderate" && (
              <ModerationQueue
                gameId={gameId!}
                myPlayerId={session.playerId}
                items={game.items}
                playerNames={playerNames}
                onDecision={refreshSubmissions}
                refreshTrigger={modRefreshTrigger}
              />
            )}
            {tab === "feed" && (
              <ActivityFeed
                submissions={submissions}
                items={game.items}
                playerNames={playerNames}
                myPlayerId={session.playerId}
                gameId={gameId!}
                canInteract={gameStatus === "active" || gameStatus === "ended"}
              />
            )}
            {tab === "players" && (
              <PlayerList
                gameId={gameId!}
                players={players}
                isOwner={isOwner}
                onUpdate={refreshPlayers}
              />
            )}
          </div>

          {/* Share Recap — visible when game has ended */}
          {(gameStatus === "ended" || gameStatus === "done") && (
            <ShareRecap
              gameTitle={game.title}
              standings={standings}
              submissions={submissions}
              items={game.items}
              playerNames={playerNames}
              currentPlayerName={session.displayName}
            />
          )}

          {/* Owner actions */}
          {isOwner && gameStatus === "active" && (
            <Button
              variant="danger"
              onClick={() => setConfirmAction("end")}
              className="mt-6 w-full"
            >
              <Square className="h-4 w-4" />
              End Game
            </Button>
          )}
          {isOwner && gameStatus === "ended" && (
            <Button
              variant="primary"
              onClick={() => setConfirmAction("done")}
              className="mt-6 w-full"
            >
              <CheckSquare className="h-4 w-4" />
              Mark as Done
            </Button>
          )}
          {!isOwner && (
            <button
              onClick={() => setConfirmAction("leave")}
              className="mt-6 inline-flex w-full items-center justify-center gap-1.5 text-center text-sm text-text-secondary hover:text-error"
            >
              <LogOut className="h-3.5 w-3.5" />
              Leave Game
            </button>
          )}
        </>
      )}

      {/* Purged state */}
      {gameStatus === "purged" && (
        <div className="mt-8 flex flex-col items-center py-12 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-sunken">
            <AlertTriangle className="h-8 w-8 text-text-secondary" />
          </div>
          <p className="mt-4 text-lg font-bold text-text">Game Expired</p>
          <p className="mt-1 text-text-secondary">
            This game has been cleaned up and is no longer available.
          </p>
          <Button
            variant="ghost"
            onClick={() => {
              removeSession(gameId!);
              navigate("/");
            }}
            className="mt-4"
          >
            <Home className="h-4 w-4" />
            Remove & Go Home
          </Button>
        </div>
      )}

      {/* Camera overlay */}
      {captureItem && captureItemObj && (
        <CameraCapture
          gameId={gameId!}
          itemId={captureItem}
          itemName={captureItemObj.name}
          onComplete={() => {
            setCaptureItem(null);
            refreshSubmissions();
          }}
          onCancel={() => setCaptureItem(null)}
        />
      )}

      {/* Confirmation modal */}
      <Modal
        open={confirmAction !== null}
        error={actionError}
        title={
          confirmAction === "solo-mod-warn"
            ? "Solo Moderator Warning"
            : confirmAction === "start"
              ? "Start Game?"
              : confirmAction === "end"
                ? "End Game?"
                : confirmAction === "done"
                  ? "Mark as Done?"
                  : confirmAction === "cancel"
                    ? "Cancel Game?"
                    : "Leave Game?"
        }
        message={
          confirmAction === "solo-mod-warn"
            ? "You're the only moderator and also a player. Your own submissions won't be able to be approved. Consider adding another moderator or switching to 'No moderation' mode."
            : confirmAction === "start"
              ? "Players will be able to start submitting photos. You can still change moderators during the game."
              : confirmAction === "end"
                ? "No more submissions will be accepted. You can continue moderating existing submissions."
                : confirmAction === "done"
                  ? "The game will be finalized. Results will be viewable for a limited time before cleanup."
                  : confirmAction === "cancel"
                    ? "This will cancel the game. Players who joined will no longer be able to access it."
                    : "This will remove the game from your list. You can rejoin with the invite code."
        }
        confirmLabel={
          confirmAction === "solo-mod-warn"
            ? "Start Anyway"
            : confirmAction === "cancel"
              ? "Cancel Game"
              : confirmAction === "leave"
                ? "Leave"
                : confirmAction === "start"
                  ? "Start"
                  : confirmAction === "end"
                    ? "End Game"
                    : "Mark Done"
        }
        confirmVariant={
          confirmAction === "leave" ||
          confirmAction === "end" ||
          confirmAction === "cancel"
            ? "danger"
            : "primary"
        }
        onConfirm={executeAction}
        onCancel={() => {
          setConfirmAction(null);
          setActionError(null);
        }}
        loading={actionLoading}
      />
    </div>
  );
}
